import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createProjectGroups,
  deleteProjectGroups,
  deleteProjectGroupsFiles,
  downloadProjectGroupsFiles,
  inviteProjectGroupsMembers,
  readProjectGroups,
  readProjectGroupsAll,
  readProjectGroupsByTaskId,
  readProjectGroupsTasksAll,
  removeProjectGroupsMembers,
  saveProjectGroupsBidNotice,
  searchProjectGroupsBidNotice,
  updateProjectGroups,
  uploadProjectGroupsFiles,
} from "@/services/api/project";
import {
  CreateProjectGroupsRequest,
  DeleteProjectGroupsFilesRequest,
  DownloadProjectGroupsFilesRequest,
  InviteProjectGroupsMembersRequest,
  ReadProjectGroupsAllRequest,
  ReadProjectGroupsTasksAllRequest,
  RemoveProjectGroupsMembersRequest,
  SaveProjectGroupsBidNoticeRequest,
  SearchProjectGroupsBidNoticeRequest,
  UpdateProjectGroupsRequest,
  UploadProjectGroupsFilesRequest,
} from "@/types/project";
import { QUERY_KEYS } from "../queryKeys";
import { useHandledQuery } from "./useHandledQuery";

// 전체 프로젝트 그룹 조회
export const useReadProjectGroupsAll = (data: ReadProjectGroupsAllRequest) => {
  const queryKey = data?.project
    ? QUERY_KEYS.PROJECT_GROUPS.LIST_BY_PROJECT(data.project.project_id)
    : QUERY_KEYS.PROJECT_GROUPS.LIST();

  return useQuery({
    queryKey,
    queryFn: () => readProjectGroupsAll({ data }),
  });
};

// 전체 프로젝트 작업 목록 조회
export const useReadProjectGroupsTasksAll = (
  data: ReadProjectGroupsTasksAllRequest
) => {
  return useQuery({
    queryKey: QUERY_KEYS.TASKS.ALL(),
    queryFn: () => readProjectGroupsTasksAll({ data }),
  });
};

// 작업 ID로 프로젝트 그룹 조회
export const useReadProjectGroupsByTaskId = (task_id: string) => {
  return useQuery({
    queryKey: QUERY_KEYS.PROJECT_GROUPS.SEARCH_BY_TASK_ID(task_id),
    queryFn: () => readProjectGroupsByTaskId({ task_id }),
  });
};

// 프로젝트 그룹 생성
export const useCreateProjectGroups = ({
  project_id,
  project_type,
}: {
  project_id: string;
  project_type: string;
}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Omit<CreateProjectGroupsRequest, "projects">) => {
      const body = {
        ...data,
        projects: [
          {
            project_id,
            project_type,
          },
        ],
      };
      return createProjectGroups({ data: body });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.PROJECT_GROUPS.LIST(),
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.PROJECT_GROUPS.LIST_BY_PROJECT(project_id),
      });
    },
  });
};

// 단일 프로젝트 그룹 조회
export const useReadProjectGroups = (
  group_id: string,
  suppressErrorToast?: boolean
) => {
  return useHandledQuery({
    queryKey: QUERY_KEYS.PROJECT_GROUPS.DETAIL(group_id),
    queryFn: () => readProjectGroups({ group_id }),
    config: { suppressErrorToast },
  });
};

// 프로젝트 그룹 정보 수정
export const useUpdateProjectGroups = (group_id: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateProjectGroupsRequest) =>
      updateProjectGroups({ group_id, data }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.PROJECT_GROUPS.DETAIL(group_id),
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.PROJECT_GROUPS.LIST(),
      });
    },
  });
};

// 프로젝트 그룹 삭제
export const useDeleteProjectGroups = (group_id: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => deleteProjectGroups({ group_id }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.PROJECT_GROUPS.LIST(),
      });
    },
  });
};

// 프로젝트 그룹 참여자 초대
export const useInviteProjectGroupsMembers = (group_id: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: InviteProjectGroupsMembersRequest) =>
      inviteProjectGroupsMembers({ group_id, data }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.PROJECT_GROUPS.DETAIL(group_id),
      });
    },
  });
};

// 프로젝트 그룹 참여자 제거
export const useRemoveProjectGroupsMembers = (group_id: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: RemoveProjectGroupsMembersRequest) =>
      removeProjectGroupsMembers({ group_id, data }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.PROJECT_GROUPS.DETAIL(group_id),
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.PROJECT_GROUPS.LIST(),
      });
    },
  });
};

// 프로젝트 그룹 파일 업로드
export const useUploadProjectGroupsFiles = (group_id: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UploadProjectGroupsFilesRequest) =>
      uploadProjectGroupsFiles({
        group_id,
        data,
      }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.PROJECT_GROUPS.DETAIL(group_id),
      });
    },
  });
};

// 프로젝트 그룹 파일 다운로드
export const useDownloadProjectGroupsFiles = (group_id: string) => {
  return useMutation({
    mutationFn: (data: DownloadProjectGroupsFilesRequest) =>
      downloadProjectGroupsFiles({ group_id, data }),
  });
};

// 프로젝트 그룹 파일 삭제
export const useDeleteProjectGroupsFiles = (group_id: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: DeleteProjectGroupsFilesRequest) =>
      deleteProjectGroupsFiles({ group_id, data }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.PROJECT_GROUPS.DETAIL(group_id),
      });
    },
  });
};

// 프로젝트 그룹 공고 검색
export const useSearchProjectGroupsBidNotice = (
  group_id: string,
  data: SearchProjectGroupsBidNoticeRequest
) => {
  return useQuery({
    queryKey: QUERY_KEYS.PROJECT_GROUPS.BID_NOTICE(group_id),
    queryFn: () => searchProjectGroupsBidNotice({ group_id, data }),
    enabled: false,
  });
};

// 프로젝트 그룹 공고 검색 쿼리 초기화
export const useResetSearchProjectGroupsBidNotice = (group_id: string) => {
  const queryClient = useQueryClient();

  return () => {
    queryClient.removeQueries({
      queryKey: QUERY_KEYS.PROJECT_GROUPS.BID_NOTICE(group_id),
    });
  };
};

// 프로젝트 그룹 공고 저장
export const useSaveProjectGroupsBidNotice = (group_id: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: SaveProjectGroupsBidNoticeRequest) =>
      saveProjectGroupsBidNotice({ group_id, data }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.PROJECT_GROUPS.DETAIL(group_id),
      });
    },
  });
};
